import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { findByName, findVehicleType, isRefrigerator, parseDate } from "../../../../utils/bulkOrderHelpers";

import CustomSelect from "../../../../components/custom/custom-select/CustomSelect";
import CustomInput from "../../../../components/custom/custom-input/CustomInput";
import CustomDatePicker from "../../../../components/shared/date-picker/Date/CustomDatePicker";
import AddYourCity from "../../../../components/orders/add-your-city/AddYourCity";
import AddCategoryModal from "../../../../components/orders/add-new-category/AddCategoryModal/AddCategoryModal";

import CloseIcon from "../../../../components/shared/icons/CloseIcon";
import CalendarIcon from "../../../../components/shared/icons/CalendarIcon";

import TenderDateTimePicker from "./TenderDateTimePicker";
import style from "./EditRowModal.module.scss";
import { CURRENCIES } from "../../../../mock";

const CURRENCY_OPTIONS = Object.values(CURRENCIES).map((c) => ({
  id: c.code,
  name: `${c.symbol} ${c.code}`,
}));

const EditRowModal = ({ row, rowIndex, onClose, onSave, directories }) => {
  const { t } = useTranslation();
  const {
    countries,
    types,
    categories,
    loadCountries,
    loadTypes,
    loadCategories,
    setCategories,
    loadCities,
    refreshCities,
  } = directories;

  const currencyOptions = useMemo(
    () => [{ id: "all", name: t("analytics.allCurrencies") }, ...CURRENCY_OPTIONS],
    [t],
  );

  // Форма
  const [shippingDate, setShippingDate] = useState(parseDate(row?.shippingDate));
  const [countryFrom, setCountryFrom] = useState(null);
  const [cityFrom, setCityFrom] = useState(null);
  const [addressFrom, setAddressFrom] = useState(row?.addressFrom || "");
  const [countryTo, setCountryTo] = useState(null);
  const [cityTo, setCityTo] = useState(null);
  const [addressTo, setAddressTo] = useState(row?.addressTo || "");
  const [category, setCategory] = useState(null);
  const [type, setType] = useState(null);
  const [temperature, setTemperature] = useState(row?.temperature || "");
  const [weight, setWeight] = useState(row?.weight ?? "");
  const [tnvedCode, setTnvedCode] = useState(row?.tnvedCode || "");
  const [referenceNumber, setReferenceNumber] = useState(row?.referenceNumber || "");
  const [currency, setCurrency] = useState(
    () => currencyOptions.find((c) => c.id === (row?.currency || "all")) || null,
  );
  const [tenderDate, setTenderDate] = useState(() => {
    if (!row?.tenderDateEnd) return null;
    const m = moment(row.tenderDateEnd);
    return m.isValid() ? m.toDate() : null;
  });
  const [tenderTime, setTenderTime] = useState(() => {
    if (!row?.tenderDateEnd) return null;
    const m = moment(row.tenderDateEnd);
    return m.isValid() ? m.toDate() : null;
  });
  const [errors, setErrors] = useState({});

  const [citiesFrom, setCitiesFrom] = useState([]);
  const [citiesTo, setCitiesTo] = useState([]);

  const [pendingCityFrom, setPendingCityFrom] = useState(row?.cityFrom || "");
  const [pendingCityTo, setPendingCityTo] = useState(row?.cityTo || "");

  const [isOpenAddCity, setIsOpenAddCity] = useState(false);
  const [addCityTarget, setAddCityTarget] = useState("from");
  const [initialCityName, setInitialCityName] = useState("");
  const [citiesRefreshCountryId, setCitiesRefreshCountryId] = useState(null);
  const [addedCityId, setAddedCityId] = useState(null);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [addCategoryPromise, setAddCategoryPromise] = useState(null);
  const [currentSearchValue, setCurrentSearchValue] = useState("");

  const inited = useRef(false);

  const setError = (key, message) =>
    setErrors((prev) => ({ ...prev, [key]: message }));

  useEffect(() => {
    loadCountries().catch(() => toast.error(t("bulkCreate.editToasts.loadCountriesError")));
    loadTypes().catch(() => toast.error(t("bulkCreate.editToasts.loadTypesError")));
  }, [loadCountries, loadTypes, t]);

  useEffect(() => {
    let active = true;
    loadCategories()
      .then((list) => {
        if (!active) return;
        const code = row?.category;
        const matched = code ? list.find((c) => c.value === code) : null;
        if (matched) {
          setCategory(matched);
        } else if (row?.categoryName || row?.category) {
          setCategory({
            id: code || row?.categoryName,
            name: row?.categoryName || code,
            value: code || "",
          });
        }
      })
      .catch(() => active && toast.error(t("bulkCreate.editToasts.loadCategoriesError")));
    return () => {
      active = false;
    };
  }, [loadCategories, row, t]);

  useEffect(() => {
    if (inited.current) return;
    if (!countries.length || !types.length) return;
    inited.current = true;

    const cf = findByName(countries, row?.countryFromName);
    if (cf) setCountryFrom(cf);
    else if (row?.countryFromName)
      setError("countryFrom", t("bulkCreate.errors.countryNotFound"));

    const ct = findByName(countries, row?.countryToName);
    if (ct) setCountryTo(ct);
    else if (row?.countryToName)
      setError("countryTo", t("bulkCreate.errors.countryNotFound"));

    const tp = findVehicleType(types, row?.vehicleType, row?.vehicleTypeValue ?? row?.vehicleType);
    if (tp) setType(tp);
    else if (row?.vehicleType)
      setError("type", t("bulkCreate.errors.typeNotFound"));
  }, [countries, types, row]);

  useEffect(() => {
    if (!countryFrom?.id) {
      setCitiesFrom([]);
      return undefined;
    }
    let active = true;
    loadCities(countryFrom.id)
      .then((list) => {
        if (!active) return;
        setCitiesFrom(list);
        if (pendingCityFrom) {
          const found = findByName(list, pendingCityFrom);
          if (found) {
            setCityFrom(found);
            setError("cityFrom", undefined);
          } else {
            setError(
              "cityFrom",
              t("bulkCreate.errors.cityNotFound"),
            );
          }
          setPendingCityFrom("");
        }
      })
      .catch(() => active && toast.error(t("bulkCreate.editToasts.loadCitiesFromError")));
    return () => {
      active = false;
    };
  }, [countryFrom, loadCities]);

  useEffect(() => {
    if (!countryTo?.id) {
      setCitiesTo([]);
      return undefined;
    }
    let active = true;
    loadCities(countryTo.id)
      .then((list) => {
        if (!active) return;
        setCitiesTo(list);
        if (pendingCityTo) {
          const found = findByName(list, pendingCityTo);
          if (found) {
            setCityTo(found);
            setError("cityTo", undefined);
          } else {
            setError(
              "cityTo",
              t("bulkCreate.errors.cityNotFound"),
            );
          }
          setPendingCityTo("");
        }
      })
      .catch(() => active && toast.error(t("bulkCreate.editToasts.loadCitiesToError")));
    return () => {
      active = false;
    };
  }, [countryTo, loadCities]);

  useEffect(() => {
    if (!citiesRefreshCountryId) return;
    refreshCities(citiesRefreshCountryId)
      .then((list) => {
        if (addCityTarget === "from") setCitiesFrom(list);
        else setCitiesTo(list);

        const id =
          addedCityId && typeof addedCityId === "object" ? addedCityId.id : addedCityId;
        const found = id ? list.find((c) => c.id === id) : null;
        if (found) {
          if (addCityTarget === "from") {
            setCityFrom(found);
            setError("cityFrom", undefined);
          } else {
            setCityTo(found);
            setError("cityTo", undefined);
          }
        }
      })
      .catch(() => toast.error(t("bulkCreate.editToasts.refreshCitiesError")))
      .finally(() => {
        setCitiesRefreshCountryId(null);
        setAddedCityId(null);
      });
  }, [citiesRefreshCountryId]);

  const openAddCity = (target, searchValue) => {
    setAddCityTarget(target);
    setInitialCityName(searchValue);
    setIsOpenAddCity(true);
  };

  const handleAddCategoryClick = (searchValue) => {
    setCurrentSearchValue(searchValue || "");
    setShowCategoryModal(true);
    return new Promise((resolve, reject) => {
      setAddCategoryPromise({ resolve, reject });
    });
  };

  const handleCategoryAdded = (newCategory) => {
    setShowCategoryModal(false);
    if (addCategoryPromise) {
      addCategoryPromise.resolve(newCategory);
      setAddCategoryPromise(null);
    }
    setCurrentSearchValue("");
    setError("category", undefined);
  };

  const validate = () => {
    const e = {};
    if (!shippingDate) e.shippingDate = t("bulkCreate.errors.shippingDateRequired");
    if (!countryFrom?.id) e.countryFrom = t("bulkCreate.errors.countryFromRequired");
    if (!cityFrom?.id) e.cityFrom = t("bulkCreate.errors.cityFromRequired");
    if (!addressFrom.trim()) e.addressFrom = t("bulkCreate.errors.addressFromRequired");
    if (!countryTo?.id) e.countryTo = t("bulkCreate.errors.countryToRequired");
    if (!cityTo?.id) e.cityTo = t("bulkCreate.errors.cityToRequired");
    if (!addressTo.trim()) e.addressTo = t("bulkCreate.errors.addressToRequired");
    if (!category?.value && !category?.name) e.category = t("bulkCreate.errors.categoryRequired");
    if (!type?.id) e.type = t("bulkCreate.errors.typeRequired");
    if (weight === "" || weight === null) e.weight = t("bulkCreate.errors.weightRequired");
    if (isRefrigerator(type) && !temperature.toString().trim())
      e.temperature = t("bulkCreate.errors.temperatureRequired");
    if (!tenderDate) e.tenderDate = t("bulkCreate.errors.tenderDateRequired");
    if (!tenderTime) e.tenderTime = t("bulkCreate.errors.tenderTimeRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      toast.error(t("bulkCreate.editToasts.validateError"));
      return;
    }

    const tenderDateEnd =
      tenderDate && tenderTime
        ? moment(tenderDate)
            .set({
              hour: moment(tenderTime).hour(),
              minute: moment(tenderTime).minute(),
              second: 0,
              millisecond: 0,
            })
            .utc()
            .format()
        : tenderDate
          ? moment(tenderDate).utc().format()
          : null;

    const updatedRow = {
      ...row,
      shippingDate: shippingDate
        ? moment(shippingDate).format("MM/DD/YYYY HH:mm:ss")
        : row.shippingDate,
      category: category?.value ?? row?.category ?? "",
      categoryName: category?.name ?? row?.categoryName ?? "",
      weight: weight === "" ? row.weight : Number(weight),
      addressFrom: addressFrom.trim(),
      countryFromName: countryFrom?.name || "",
      cityFrom: cityFrom?.name || "",
      addressTo: addressTo.trim(),
      countryToName: countryTo?.name || "",
      cityTo: cityTo?.name || "",
      vehicleType: type?.name || "",
      vehicleTypeValue: type?.value ?? null,
      temperature,
      tnvedCode,
      referenceNumber,
      currency: currency?.id && currency.id !== "all" ? currency.id : "",
      countryIdFrom: countryFrom?.id,
      cityIdFrom: cityFrom?.id,
      countryIdTo: countryTo?.id,
      cityIdTo: cityTo?.id,
      tenderDateEnd,
      __errors: {},
      __valid: true,
    };

    onSave(rowIndex, updatedRow);
    toast.success(t("bulkCreate.editToasts.saved"));
    onClose();
  };

  if (!row) return null;

  return (
    <div
      className={style.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={style.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("bulkCreate.editRow.title", { number: rowIndex + 1 })}</span>
          <button type="button" className={style.closeBtn} onClick={onClose}>
            <CloseIcon width={20} height={20} fill="#6D7885" />
          </button>
        </div>

        <div className={style.body}>
          <div className={style.section}>
            <div className={style.divider}>
              <span className={style.dividerLabel}>{t("bulkCreate.editRow.sections.loading")}</span>
            </div>

            <div className={style.field}>
              <span className={style.fieldLabel}>{t("bulkCreate.fields.shippingDate")}</span>
              <CustomDatePicker
                selected={shippingDate}
                onChange={setShippingDate}
                dateFormat="dd.MM.yyyy"
                placeholderText={t("bulkCreate.placeholders.date")}
                bgGray
                withBorder
                IconStart={<CalendarIcon />}
                popperProps={{ strategy: "fixed" }}
              />
              {errors.shippingDate && (
                <span className={style.fieldError}>{errors.shippingDate}</span>
              )}
            </div>

            <div className={style.row}>
              <div className={style.col}>
                <CustomSelect
                  usePortal
                  returnObject
                  labelText={t("bulkCreate.fields.countryFrom")}
                  placeholder={t("bulkCreate.placeholders.selectCountry")}
                  options={countries}
                  defaultValue={countryFrom}
                  errorText={errors.countryFrom}
                  onSubmit={(item) => {
                    setCountryFrom(item);
                    setCityFrom(null);
                    setPendingCityFrom("");
                    setError("countryFrom", undefined);
                  }}
                />
              </div>
              <div className={style.col}>
                <CustomSelect
                  usePortal
                  returnObject
                  labelText={t("bulkCreate.fields.cityFrom")}
                  placeholder={t("bulkCreate.placeholders.selectCity")}
                  options={citiesFrom}
                  defaultValue={cityFrom}
                  errorText={errors.cityFrom}
                  showNoOptionsMessage
                  onNoOptionsClick={(searchValue) => openAddCity("from", searchValue)}
                  onSubmit={(item) => {
                    setCityFrom(item);
                    setError("cityFrom", undefined);
                  }}
                />
              </div>
            </div>

            <div className={style.full}>
              <CustomInput
                isBgGray
                labelText={t("bulkCreate.fields.addressFrom")}
                value={addressFrom}
                errorText={errors.addressFrom}
                onChangeValue={setAddressFrom}
              />
            </div>
          </div>

          <div className={style.section}>
            <div className={style.divider}>
              <span className={style.dividerLabel}>{t("bulkCreate.editRow.sections.unloading")}</span>
            </div>

            <div className={style.row}>
              <div className={style.col}>
                <CustomSelect
                  usePortal
                  returnObject
                  labelText={t("bulkCreate.fields.countryTo")}
                  placeholder={t("bulkCreate.placeholders.selectCountry")}
                  options={countries}
                  defaultValue={countryTo}
                  errorText={errors.countryTo}
                  onSubmit={(item) => {
                    setCountryTo(item);
                    setCityTo(null);
                    setPendingCityTo("");
                    setError("countryTo", undefined);
                  }}
                />
              </div>
              <div className={style.col}>
                <CustomSelect
                  usePortal
                  returnObject
                  labelText={t("bulkCreate.fields.cityTo")}
                  placeholder={t("bulkCreate.placeholders.selectCity")}
                  options={citiesTo}
                  defaultValue={cityTo}
                  errorText={errors.cityTo}
                  showNoOptionsMessage
                  onNoOptionsClick={(searchValue) => openAddCity("to", searchValue)}
                  onSubmit={(item) => {
                    setCityTo(item);
                    setError("cityTo", undefined);
                  }}
                />
              </div>
            </div>

            <div className={style.full}>
              <CustomInput
                isBgGray
                labelText={t("bulkCreate.fields.addressTo")}
                value={addressTo}
                errorText={errors.addressTo}
                onChangeValue={setAddressTo}
              />
            </div>
          </div>

          <div className={style.section}>
            <div className={style.divider}>
              <span className={style.dividerLabel}>{t("bulkCreate.editRow.sections.excelData")}</span>
            </div>

            <div className={style.full}>
              <CustomSelect
                usePortal
                key={categories.length}
                labelText={t("bulkCreate.fields.category")}
                placeholder={t("bulkCreate.fields.category")}
                errorText={errors.category}
                options={categories}
                defaultValue={category}
                onSubmit={(item) => {
                  setCategory(item);
                  setError("category", undefined);
                }}
                returnObject
                showAddCategoryButton
                onOptionsUpdate={setCategories}
                onAddCategoryClick={handleAddCategoryClick}
                addCategoryButtonText={t("bulkCreate.fields.addCategory")}
              />
              {showCategoryModal && (
                <AddCategoryModal
                  onClose={() => {
                    setShowCategoryModal(false);
                    if (addCategoryPromise) {
                      addCategoryPromise.reject(new Error("User canceled"));
                      setAddCategoryPromise(null);
                    }
                    setCurrentSearchValue("");
                  }}
                  onCategoryAdded={handleCategoryAdded}
                  initialName={currentSearchValue}
                />
              )}
            </div>

            <div className={style.full}>
              <CustomSelect
                usePortal
                returnObject
                labelText={t("bulkCreate.fields.vehicleType")}
                placeholder={t("bulkCreate.placeholders.selectVehicleType")}
                options={types}
                defaultValue={type}
                errorText={errors.type}
                onSubmit={(item) => {
                  setType(item);
                  setError("type", undefined);
                }}
              />
            </div>

            <div className={style.full}>
              <CustomInput
                isBgGray
                labelText={t("bulkCreate.fields.temperature")}
                value={temperature}
                errorText={errors.temperature}
                onChangeValue={setTemperature}
              />
            </div>

            <div className={style.row}>
              <div className={style.col}>
                <CustomInput
                  isBgGray
                  inputType="number"
                  labelText={t("bulkCreate.fields.weight")}
                  value={weight}
                  errorText={errors.weight}
                  onChangeValue={setWeight}
                />
              </div>
              <div className={style.col}>
                <CustomInput
                  isBgGray
                  labelText={t("bulkCreate.fields.tnved")}
                  value={tnvedCode}
                  onChangeValue={setTnvedCode}
                />
              </div>
            </div>

            <div className={style.row}>
              <div className={style.row}>
                <CustomInput
                  isBgGray
                  labelText={t("bulkCreate.fields.referenceNumber")}
                  value={referenceNumber}
                  onChangeValue={setReferenceNumber}
                />
              </div>
              <div className={style.row}>
                <CustomSelect
                  usePortal
                  returnObject
                  labelText={t('bulkCreate.table.bidCurrency')}
                  placeholder={t("orderFilter.currencyPlaceholder")}
                  options={currencyOptions}
                  defaultValue={currency}
                  errorText={errors.currency}
                  onSubmit={(item) => {
                    setCurrency(item);
                    setError("currency", undefined);
                  }}
                />
              </div>
            </div>
            <TenderDateTimePicker
              date={tenderDate}
              time={tenderTime}
              minDate={new Date()}
              errorDate={errors.tenderDate}
              errorTime={errors.tenderTime}
              setDate={(d) => {
                setTenderDate(d);
                setError("tenderDate", undefined);
              }}
              setTime={(t) => {
                setTenderTime(t);
                setError("tenderTime", undefined);
              }}
            />
          </div>
        </div>

        <div className={style.footer}>
          <button type="button" className={style.saveBtn} onClick={handleSave}>
            {t("bulkCreate.editRow.save")}
          </button>
        </div>
      </div>

      <AddYourCity
        countries={countries}
        country={addCityTarget === "from" ? countryFrom : countryTo}
        setCountry={
          addCityTarget === "from"
            ? (c) => {
                setCountryFrom(c);
                setCityFrom(null);
              }
            : (c) => {
                setCountryTo(c);
                setCityTo(null);
              }
        }
        setCity={addCityTarget === "from" ? setCityFrom : setCityTo}
        setIsСallСities={setCitiesRefreshCountryId}
        setAddedCityId={setAddedCityId}
        isOpenAdd={isOpenAddCity}
        setIsOpenAdd={setIsOpenAddCity}
        initialCityName={initialCityName}
      />
    </div>
  );
};

export default EditRowModal;
